/**
 * Development Authentication Codes
 * 
 * These codes can be entered during phone verification to instantly
 * authenticate as specific roles without SMS verification.
 * 
 * Only works in development/localhost environments.
 *
 * Mock patient (code 100010): Use for check-in and ChartMind. Log in with 100010
 * to get a patient with populated CMR; then use "Link mock patient (dev)" in
 * ChartMind (as professional-chartmind) to link without going through the queue.
 */

/**
 * Development Facility/Clinic ID
 * Used for dev accounts that need to be associated with a clinic/facility
 * This is a fixed ID that all dev accounts share for testing purposes
 */
export const DEV_CLINIC_ID = 'dev-clinic-001';
export const DEV_FACILITY_ID = 'dev-clinic-001'; // Alias for clarity

/**
 * Development Department ID
 * Used for dev accounts that need to be associated with a department
 */
export const DEV_DEPARTMENT_ID = 'dev-dept-001';

/**
 * Development Clinic/Template ID
 * Used for dev accounts that need to be associated with a specific clinic (template)
 */
export const DEV_TEMPLATE_ID = 'dev-template-001';

/** Dev auth code for mock patient with populated CMR (check-in + ChartMind). */
export const DEV_MOCK_PATIENT_CODE = '100010';

/**
 * True when dev auth codes and mock patient flow are allowed (localhost + Firebase experimental hosts).
 * Use this for "Link mock patient (dev)" so it works wherever dev auth works.
 */
export const isDevAuthEnvironment = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('192.168.') ||
    window.location.hostname === 'hlthdsk-experimental.web.app' ||
    window.location.hostname === 'hlthdsk-experimental.firebaseapp.com' ||
    window.location.hostname === 'hlthdsk-experimental-demo.web.app' ||
    window.location.hostname === 'hlthdsk-experimental-demo.firebaseapp.com' ||
    process.env.REACT_APP_ENVIRONMENT === 'experimental'
  );
};

const isDevEnvironment = isDevAuthEnvironment;

export const DEV_AUTH_CODES = {
  // Patient roles
  '100001': {
    role: 'patient',
    name: 'Test Patient',
    email: 'patient@dev.local',
    description:
      'Basic patient account. Firestore isDevAccount bypasses client subscription gates (useAuth tier / symptom voice FeatureGate, diagnostic tests route, Phase 4 continue).',
  },
  /**
   * Mock patient with populated CMR for check-in and ChartMind.
   * Use for check-in: log in with this code, go to /checkin/:clinicId/:code, submit.
   * For ChartMind: after mock patient checks in, use queue "Start ChartMind"; or use dev-only "Link mock patient (dev)" (after logging in once as this patient to set devMockPatientUid in localStorage).
   */
  '100010': {
    role: 'patient',
    name: 'John Smith',
    email: 'mock-patient@dev.local',
    description: 'Mock patient with populated CMR for check-in and ChartMind',
    isMockPatient: true,
  },
  /** Use on /auth?returnTo=/symptomchecker for instant sign-in to standalone symptom checker (no SMS). */
  '100011': {
    role: 'patient',
    name: 'Symptom Checker Dev',
    email: 'symptomchecker@dev.local',
    description: 'Standalone symptom checker (dev) – use at /auth?returnTo=/symptomchecker',
  },
  /** Use on /auth?returnTo=/chartmind for instant sign-in to standalone ChartMind (no SMS). */
  '100012': {
    role: 'professional-chartmind',
    name: 'ChartMind Standalone Dev',
    email: 'chartmind-standalone@dev.local',
    description: 'Standalone ChartMind (dev) – use at /auth?returnTo=/chartmind',
    facilityId: 'dev-clinic-001',
    departmentId: 'dev-dept-001',
  },
  /** Use on /auth?returnTo=/getonthemap for instant sign-in to standalone Get on the map (no SMS). */
  '100013': {
    role: 'facility',
    name: 'Get on the Map Dev',
    email: 'getonthemap@dev.local',
    description: 'Standalone Get on the map (dev) – use at /auth?returnTo=/getonthemap',
    facilityId: 'dev-clinic-001',
  },
  '100002': {
    role: 'student',
    name: 'Test Student',
    email: 'student@dev.local',
    description: 'Student account',
  },
  /** P2 role: identical to patient (dev auth code 100003; 100002 is student). */
  '100003': {
    role: 'p2',
    name: 'Test P2',
    email: 'p2@dev.local',
    description: 'P2 account (same as patient)',
  },
  /** P4 role: identical to p2/patient (dev auth code 100004). */
  '100004': {
    role: 'p4',
    name: 'Test P4',
    email: 'p4@dev.local',
    description: 'P4 account (same as patient / p2)',
  },

  // Professional roles
  '200001': {
    role: 'professional',
    name: 'Test Professional',
    email: 'professional@dev.local',
    description: 'General healthcare professional',
  },
  '200002': {
    role: 'unused-professional-chartmind',
    name: 'Test ChartMind Pro',
    email: 'chartmind-pro@dev.local',
    description: 'ChartMind professional user (unused - use 300003 instead)',
  },
  
  // ChartMind specific roles
  '300003': {
    role: 'professional-chartmind',
    name: 'ChartMind Provider',
    email: 'chartmind-provider@dev.local',
    description: 'ChartMind professional - read-only queue access',
    facilityId: 'dev-clinic-001',
    departmentId: 'dev-dept-001', // Associated with dev department
  },
  '300004': {
    role: 'chartmind-supervisor',
    name: 'Clinic Manager',
    email: 'clinic-supervisor@dev.local',
    description: 'Clinic Manager - manages a single clinic within a department',
    facilityId: 'dev-clinic-001',
    departmentId: 'dev-dept-001',
    clinicId: 'dev-template-001', // Specific clinic this supervisor manages
  },
  '300005': {
    role: 'chartmind-admin',
    name: 'ChartMind Admin',
    email: 'chartmind-admin@dev.local',
    description: 'ChartMind administrator - manages prompts for designees',
    facilityId: 'dev-clinic-001',
  },
  '300006': {
    role: 'chartmind-kijabe',
    name: 'ChartMind Kijabe Admin',
    email: 'chartmind-kijabe@dev.local',
    description: 'ChartMind Kijabe administrator - mirrors ChartMind admin permissions',
    facilityId: 'dev-clinic-001',
  },
  
  // Department Admin - manages all clinics in their department
  '300007': {
    role: 'department-admin',
    name: 'Department Admin',
    email: 'department-admin@dev.local',
    description: 'Manages all clinics within their department',
    facilityId: 'dev-clinic-001',
    departmentId: 'dev-dept-001',
  },
  
  // Orphaned Professional - no facility assignment (for testing join request flow)
  '300008': {
    role: 'professional-chartmind',
    name: 'Orphaned Professional',
    email: 'orphan-pro@dev.local',
    description: 'Professional without a facility - for testing join request',
    // Note: No facilityId - this user needs to request to join
  },
  
  // Multi-facility User - belongs to multiple facilities (for testing facility switching)
  '300009': {
    role: 'professional-chartmind',
    name: 'Multi-Facility Pro',
    email: 'multi-facility@dev.local',
    description: 'Professional with multiple facility assignments',
    facilityId: 'dev-clinic-001',
    departmentId: 'dev-dept-001',
    // Additional facilities would be added via staff designation
    facilities: [
      { facilityId: 'dev-clinic-001', departmentId: 'dev-dept-001', role: 'professional-chartmind' },
      { facilityId: 'dev-clinic-002', departmentId: 'dev-dept-002', role: 'chartmind-supervisor' },
    ],
  },
  
  // System admin roles
  '400001': {
    role: 'system-medical-admin',
    name: 'System Admin',
    email: 'system-admin@dev.local',
    description: 'System medical administrator',
  },
  
  // Facility roles
  '500001': {
    role: 'facility',
    name: 'Test Facility',
    email: 'facility@dev.local',
    description: 'Healthcare facility account',
    facilityId: 'dev-clinic-001', // Associated with dev clinic for testing
  },
  /** F2 role: duplicate of facility with its own workspace (dev only). Use code 500002 to sign in as f2. */
  '500002': {
    role: 'f2',
    name: 'Test F2',
    email: 'f2@dev.local',
    description: 'F2 facility-style account – use at /dashboard/f2',
    facilityId: 'dev-clinic-001',
  },
  /** Urgent Care test account — facility linked to dev-clinic-uc-001. Self-designate urgent_care via AddressSettings walk-in toggle after login (dev login does not seed the locations doc). */
  '500003': {
    role: 'facility',
    name: 'Test Urgent Care Facility',
    email: 'urgent-care-facility@dev.local',
    description: 'Urgent Care facility test account — toggle walk-in in Address Settings',
    facilityId: 'dev-clinic-uc-001',
  },
  
  // Other roles
  '600001': {
    role: 'clerkship-coordinator',
    name: 'Clerkship Coordinator',
    email: 'clerkship@dev.local',
    description: 'Medical school clerkship coordinator',
  },
  '600002': {
    role: 'caremap-manager',
    name: 'CareMap Manager',
    email: 'caremap-manager@dev.local',
    description: 'CareMap system manager',
  },
  '600003': {
    role: 'medication-card-manager',
    name: 'Medication Card Manager',
    email: 'medication-card-manager@dev.local',
    description: 'Medication card management system',
  },

  // LLM Manager roles (110xxx series)
  // Global LLM Manager - can edit all prompts across the system
  '110001': {
    role: 'llm-manager-global',
    name: 'Global LLM Manager',
    email: 'llm-global-manager@dev.local',
    description: 'Global LLM Manager - full access to all prompts',
    level: 'global',
    scope: null,
    allowedViewingRoles: ['patient', 'p2', 'p4', 'professional-chartmind', 'pro2'],
  },
  // Regional LLM Managers - can edit Position-2 prompts in their regions
  '110002': {
    role: 'llm-manager-regional',
    name: 'Regional LLM Manager (East Africa)',
    email: 'llm-regional-eastafrica@dev.local',
    description: 'Regional manager for East Africa - Position 2 prompts',
    level: 'regional',
    scope: { regions: ['east_africa'], localities: [] },
    allowedViewingRoles: ['patient', 'p2', 'p4', 'professional-chartmind', 'pro2'],
  },
  '110003': {
    role: 'llm-manager-regional',
    name: 'Regional LLM Manager (Central Africa)',
    email: 'llm-regional-centralafrica@dev.local',
    description: 'Regional manager for Central Africa - Position 2 prompts',
    level: 'regional',
    scope: { regions: ['central_africa'], localities: [] },
    allowedViewingRoles: ['patient', 'p2', 'p4', 'professional-chartmind', 'pro2'],
  },
  // NOTE: llm-manager-local role removed - Position 3 management is handled by
  // chartmind-admin role (300005) for ChartMind prompts via their designee system

  // Generic Regional LLM Manager (single code for quick dev access)
  '110004': {
    role: 'llm-manager-regional',
    name: 'Regional LLM Manager',
    email: 'llm-regional@dev.local',
    description: 'Regional LLM Manager - Position 2 prompts in assigned region(s)',
    level: 'regional',
    scope: { regions: ['east_africa'], localities: [] },
    allowedViewingRoles: ['patient', 'p2', 'p4', 'professional-chartmind', 'pro2'],
  },

  // Pro2 role: duplicate of professional-chartmind with its own workspace (dev only)
  '800001': {
    role: 'pro2',
    name: 'Test Pro2',
    email: 'pro2@dev.local',
    description: 'Pro2 account – ChartMind-style workspace splash at /dashboard/chartmind-workspace (alias /dashboard/pro2)',
    facilityId: 'dev-clinic-001',
    departmentId: 'dev-dept-001',
  },

  // Professional Reviewer - reviews add-another learnings to teach the system (shadow mode)
  '700001': {
    role: 'professional-reviewer',
    name: 'Professional Reviewer',
    email: 'professional-reviewer@dev.local',
    description: 'Reviews add-another learnings to teach the system',
    facilityId: 'dev-clinic-001',
  },

  // Promotional sponsor (CareMap zip carousel; admin approval + ad subscription for live listings)
  '900001': {
    role: 'advertiser',
    name: 'Dev Promotional Sponsor',
    email: 'promo-sponsor@dev.local',
    description: 'Promotional sponsor — zip-targeted carousel editor and review flow',
  },
};

/**
 * Check if a code is a valid dev auth code
 * @param {string} code - The code to check
 * @returns {boolean}
 */
export const isDevAuthCode = (code) => {
  if (!isDevEnvironment()) return false;
  return Object.keys(DEV_AUTH_CODES).includes(code);
};

/**
 * Get user data for a dev auth code
 * @param {string} code - The dev auth code
 * @returns {Object|null} User data or null if invalid
 */
export const getDevAuthData = (code) => {
  if (!isDevAuthCode(code)) return null;
  return DEV_AUTH_CODES[code];
};

/**
 * Get all available dev codes (for display in dev console)
 * @returns {Array} Array of code info objects
 */
export const getAllDevCodes = () => {
  if (!isDevEnvironment()) return [];
  
  return Object.entries(DEV_AUTH_CODES).map(([code, data]) => ({
    code,
    ...data,
  }));
};

// Expose to browser console for easy reference
if (typeof window !== 'undefined' && isDevEnvironment()) {
  window.__DEV_AUTH_CODES__ = {
    codes: DEV_AUTH_CODES,
    list: () => {
      
      Object.entries(DEV_AUTH_CODES).forEach(([code, data]) => {
      });
      
    },
    help: () => {
    }
  };
  
  // Auto-show help on load
}

export default DEV_AUTH_CODES;
